import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { X } from 'lucide-react';
import pin from '../assets/pins.png';
import map from '../assets/map.png';
import ujjwalaLogo from '../assets/ujjwala-logo.png';
import wlpgaLogo from '../assets/wlpga-logo.png';
import ioclLogo from '../assets/iocl-logo.png';
import bpclLogo from '../assets/bpcl-logo.png';
import hpclLogo from '../assets/hpcl-logo.png';

gsap.registerPlugin(ScrollTrigger);

const TimelineJourney = () => {
  const containerRef = useRef(null);
  const roadRef = useRef(null);
  const mapRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [selectedMilestone, setSelectedMilestone] = useState(null);

  const milestones = [
    {
      year: 1987,
      description: "Journey Begins",
      position: { top: '15%', left: '71%' },
      position1000: { top: '15%', left: '69%' },
      position1200: { top: '15%', left: '70%' },
      position1400: { top: '15%', left: '71%' },
      mobilePosition: { top: '10%', left: '77%' },
      logo: null,
      detailedInfo: "Began our journey with independent sales operations and one manufacturing unit in a single Indian state."
    },
    {
      year: 1992,
      description: "Six States Expansion",
      position: { top: '21%', left: '65%' },
      position1000: { top: '21%', left: '63%' },
      position1200: { top: '21%', left: '64%' },
      position1400: { top: '21%', left: '65%' },
      mobilePosition: { top: '15%', left: '71%' },
      logo: null,
      detailedInfo: "By 1992, we had successfully expanded our operations to six states, establishing a strong presence and distribution network that would serve as the foundation for our future growth."
    },
    {
      year: 2012,
      description: "Second Generation Leadership",
      position: { top: '28%', left: '72%' },
      position1000: { top: '28%', left: '70%' },
      position1200: { top: '28%', left: '71%' },
      position1400: { top: '28%', left: '72%' },
      mobilePosition: { top: '22%', left: '73%' },
      logo: null,
      detailedInfo: "Welcomed Ms. Aarthi Kanodia, representing the second generation of leadership into the business"
    },
    {
      year: 2016,
      description: "PM Ujjwala Yojana",
      position: { top: '33%', left: '65%' },
      position1000: { top: '33%', left: '63%' },
      position1200: { top: '33%', left: '64%' },
      position1400: { top: '33%', left: '65%' },
      mobilePosition: { top: '30%', left: '71%' },
      logo: ujjwalaLogo,
      detailedInfo: "We became a proud partner in the PM Ujjwala Yojana, a flagship scheme aimed at providing clean cooking fuel to women from below poverty line households, empowering millions across India."
    },
    {
      year: 2017,
      description: "Pan-India Presence",
      position: { top: '46%', left: '66%' },
      position1000: { top: '46%', left: '64%' },
      position1200: { top: '46%', left: '65%' },
      position1400: { top: '46%', left: '66%' },
      mobilePosition: { top: '39%', left: '58%' },
      logo: null,
      detailedInfo: "Achieved nationwide coverage with operations spanning all states and union territories, making us one of the largest LPG distributors in the country."
    },
    {
      year: 2018,
      description: "World LPG Association",
      position: { top: '50%', left: '59%' },
      position1000: { top: '50%', left: '57%' },
      position1200: { top: '50%', left: '58%' },
      position1400: { top: '50%', left: '59%' },
      mobilePosition: { top: '47%', left: '68%' },
      logo: wlpgaLogo,
      detailedInfo: "Became a member of the World LPG Association, joining the global community of LPG industry leaders and contributing to international best practices and standards."
    },
    {
      year: 2019,
      description: "13 African Countries",
      position: { top: '60%', left: '34%' },
      position1000: { top: '60%', left: '32%' },
      position1200: { top: '60%', left: '33%' },
      position1400: { top: '60%', left: '34%' },
      mobilePosition: { top: '57%', left: '48%' },
      logo: null,
      detailedInfo: "Expanded our international footprint by establishing operations in 13 African countries, bringing our expertise and commitment to clean cooking fuel to new markets."
    },
    {
      year: 2000,
      description: "Oil Giants Partnership",
      position: { top: '70%', left: '29%' },
      position1000: { top: '70%', left: '27%' },
      position1200: { top: '70%', left: '28%' },
      position1400: { top: '70%', left: '29%' },
      mobilePosition: { top: '66%', left: '27%' },
      logos: [ioclLogo, bpclLogo, hpclLogo],
      detailedInfo: "Forged strategic partnerships with India's leading oil companies - IOCL, BPCL, and HPCL - strengthening our supply chain and expanding our distribution capabilities."
    },
    {
      year: 2022,
      description: "Electric Cooking Launch",
      position: { top: '75%', left: '38%' },
      position1000: { top: '75%', left: '36%' },
      position1200: { top: '75%', left: '37%' },
      position1400: { top: '75%', left: '38%' },
      mobilePosition: { top: '78%', left: '24%' },
      logo: null,
      detailedInfo: "Pioneered the future of clean cooking by launching electric cooking solutions, diversifying our portfolio and contributing to India's sustainable energy goals."
    }
  ];

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();

    const timer = setTimeout(() => {
      const road = roadRef.current;
      const container = containerRef.current;
      const mapElement = mapRef.current;
      const pins = container.querySelectorAll('.tj-pin-marker');
      
      if (!road || !container || !mapElement || pins.length === 0) return;
      
      pins.forEach((pin) => {
        const bubble = pin.querySelector('.tj-bubble');
        const content = pin.querySelector('.tj-pin-content');
        if (bubble && content) {
          gsap.set(bubble, { scale: 0, opacity: 0, y: -100 });
          gsap.set(content, { opacity: 0, y: 20, x: 0 });
        }
      });
      
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: container,
          start: 'top top',
          end: 'bottom bottom',
          pin: true,
          pinSpacing: false,
          scrub: 0.5,
          onUpdate: (self) => {
            setProgress(self.progress * 100);
          }
        }
      });

      tl.fromTo(road, 
        { 
          strokeDashoffset: 5000,
          opacity: 0.4
        },
        { 
          strokeDashoffset: 0,
          opacity: 0.8,
          ease: 'none'
        }, 
        0
      );

      pins.forEach((pin, i) => {
        const bubble = pin.querySelector('.tj-bubble');
        const content = pin.querySelector('.tj-pin-content');
        
        if (!bubble || !content) return;
        
        const totalPins = pins.length;
        const startTime = (i / totalPins) * 0.7;
        const duration = 0.2;
        
        tl.to(bubble, {
          scale: 1,
          opacity: 1,
          y: 0,
          ease: 'back.out(1.2)',
          duration: duration
        }, startTime);
        
        tl.to(content, {
          opacity: 1,
          y: 0,
          x: 0,
          ease: 'power2.out',
          duration: duration * 0.8
        }, startTime + duration * 0.3);
      });
    }, 100);

    return () => {
      clearTimeout(timer);
      ScrollTrigger.getAll().forEach(t => t.kill());
    };
  }, [isMobile]);

  return (
    <div className="tj-root" style={{ background: '#ffffff', overflow: 'hidden', minHeight: '100vh', position: 'relative', width: '100%', maxWidth: '100vw', paddingTop: '40px' }}>
      <div 
        ref={containerRef} 
        style={{
          position: 'relative',
          height: isMobile ? '500vh' : '400vh'
        }}
        className="tj-timeline-container"
      >
        <div className="tj-sticky-wrapper" style={{
          position: 'sticky',
          top: '0',
          height: '100vh',
          width: '100%',
          maxWidth: '1350px',
          margin: '0 auto',
          overflow: 'hidden',
          paddingLeft: '40px',
          paddingRight: '40px',
          boxSizing: 'border-box'
        }}>
          {/* Scroll to View Indicator */}
          <div className="tj-scroll-arrow" style={{
            position: 'absolute',
            right: isMobile ? '20px' : '40px',
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 100,
            pointerEvents: 'none',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#ff6b35',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              writingMode: 'vertical-rl',
              textOrientation: 'mixed'
            }}>Scroll To View Our Journey</span>
            <div style={{
              width: '48px',
              height: '48px',
              background: 'linear-gradient(135deg, #ff6b35, #ffa500)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(255, 107, 53, 0.3)'
            }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </div>
          </div>

          <div 
            ref={mapRef}
            className="tj-map-background"
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: '100%',
              height: '100%',
              transform: 'translate(-50%, -50%)',
              backgroundImage: `url(${map})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              opacity: 1,
              filter: 'grayscale(50%)',
              willChange: 'transform',
              boxSizing: 'border-box'
            }}
          >
            <div className="tj-map-overlay" style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: 'radial-gradient(circle at 30% 40%, rgba(255,255,255,0.2) 0%, transparent 50%), radial-gradient(circle at 70% 60%, rgba(255,255,255,0.2) 0%, transparent 50%)'
            }}></div>
          </div>

          <svg
            className="tj-road-svg"
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%) scale(0.65)',
              width: '100%',
              height: '100%',
              zIndex: 1,
              pointerEvents: 'none'
            }}
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
          >
            <defs>
              <linearGradient id="tj-roadGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" style={{ stopColor: '#C5C7C9', stopOpacity: 1 }} />
                <stop offset="58%" style={{ stopColor: '#C8CACC', stopOpacity: 1 }} />
                <stop offset="100%" style={{ stopColor: '#D1D3D4', stopOpacity: 1 }} />
              </linearGradient>
            </defs>
            <path
              ref={roadRef}
              d="M 85,2 Q 80,8 75,12 Q 70,16 82,20 Q 88,24 75,30 Q 65,36 60,40 Q 55,44 73,50 Q 80,54 55,60 Q 40,65 25,72 Q 15,78 15,85 Q 15,92 40,98"
              fill="none"
              stroke="url(#tj-roadGradient)"
              strokeWidth="3.5"
              strokeLinecap="round"
              strokeDasharray="5000"
              strokeDashoffset="5000"
              style={{
                filter: 'drop-shadow(0 4px 8px rgba(255, 126, 61, 0.3))'
              }}
            />
          </svg>

          {milestones.map((milestone, index) => {
            const currentPosition = isMobile ? milestone.mobilePosition : milestone.position;
            return (
            <div
              key={milestone.year}
              className="tj-pin-marker"
              style={{
                position: 'absolute',
                top: currentPosition.top,
                left: currentPosition.left,
                transform: 'translate(-50%, -50%)',
                zIndex: 100 + index,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}
            >
              <div
                className="tj-bubble"
                onClick={() => setSelectedMilestone(milestone)}
                style={{
                  position: 'relative',
                  transform: 'scale(0)',
                  opacity: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  willChange: 'transform, opacity',
                  cursor: 'pointer'
                }}
              >
                <div className="tj-pin-wrapper" style={{
                  position: 'relative',
                  width: '87px',
                  height: '82px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <img 
                    src={pin} 
                    alt="Pin" 
                    className="tj-pin-image"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      filter: 'drop-shadow(0 8px 16px rgba(255, 126, 61, 0.4))'
                    }}
                  />
                  <div className="tj-pin-year" style={{
                    position: 'absolute',
                    top: '25%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    color: 'white',
                    fontSize: '0.85rem',
                    fontWeight: 'bold',
                    textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                    zIndex: 10
                  }}>
                    {milestone.year}
                  </div>
                </div>
              </div>

              <div
                className="tj-pin-content"
                style={{
                  position: 'absolute',
                  top: '45%',
                  transform: 'translateY(-50%)',
                  width: milestone.logos ? '250px' : '160px',
                  padding: '12px',
                  borderRadius: '8px',
                  opacity: 0,
                  left: index % 2 === 0 ? (isMobile ? '37px' : '70px') : 'auto',
                  right: index % 2 === 1 ? (isMobile ? '37px' : '60px') : 'auto',
                  willChange: 'transform, opacity',
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '8px',
                  alignItems: 'center',
                  justifyContent: index % 2 === 0 ? 'flex-start' : 'flex-end'
                }}
              >
                {index % 2 === 1 && milestone.logo && (
                  <img 
                    src={milestone.logo} 
                    alt="Logo" 
                    style={{
                      width: isMobile ? '35px' : '40px',
                      height: 'auto',
                      objectFit: 'contain',
                      flexShrink: 0
                    }}
                  />
                )}
                
                {index % 2 === 1 && milestone.logos && (
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    flexShrink: 0
                  }}>
                    {milestone.logos.map((logo, i) => (
                      <img 
                        key={i}
                        src={logo} 
                        alt={`Logo ${i + 1}`} 
                        style={{
                          width: isMobile ? '35px' : '50px',
                          height: 'auto',
                          objectFit: 'contain'
                        }}
                      />
                    ))}
                  </div>
                )}

                <p className="tj-pin-text" style={{
                  margin: 0,
                  fontSize: '0.8rem',
                  lineHeight: '1.4',
                  color: '#333',
                  fontWeight: '600',
                  textAlign: index % 2 === 0 ? 'left' : 'right'
                }}>
                  {milestone.description}
                </p>
                
                {index % 2 === 0 && milestone.logo && (
                  <img 
                    src={milestone.logo} 
                    alt="Logo" 
                    style={{
                      width: isMobile ? '60px' : '80px',
                      height: 'auto',
                      objectFit: 'contain',
                      flexShrink: 0
                    }}
                  />
                )}
                
                {index % 2 === 0 && milestone.logos && (
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    flexShrink: 0
                  }}>
                    {milestone.logos.map((logo, i) => (
                      <img 
                        key={i}
                        src={logo} 
                        alt={`Logo ${i + 1}`} 
                        style={{
                          width: isMobile ? '35px' : '50px',
                          height: 'auto',
                          objectFit: 'contain'
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
          })}
        </div>
      </div>

      {selectedMilestone && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '20px',
            boxSizing: 'border-box'
          }}
          onClick={() => setSelectedMilestone(null)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '600px',
              width: '100%',
              position: 'relative',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'tj-popup-appear 0.3s ease-out'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedMilestone(null)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f0f0f0'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <X size={24} color="#333" />
            </button>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              marginBottom: '24px',
              flexWrap: 'wrap'
            }}>
              <div style={{
                background: 'rgb(245, 130, 32)',
                color: 'white',
                padding: '16px 24px',
                borderRadius: '12px',
                fontSize: '1.5rem',
                fontWeight: 'bold',
                boxShadow: '0 4px 12px rgba(245, 130, 32, 0.3)'
              }}>
                {selectedMilestone.year}
              </div>
              
              {selectedMilestone.logo && (
                <img 
                  src={selectedMilestone.logo} 
                  alt="Logo" 
                  style={{
                    width: '100px',
                    height: 'auto',
                    objectFit: 'contain'
                  }}
                />
              )}
              
              {selectedMilestone.logos && (
                <div style={{
                  display: 'flex',
                  gap: '12px',
                  flexWrap: 'wrap'
                }}>
                  {selectedMilestone.logos.map((logo, i) => (
                    <img 
                      key={i}
                      src={logo} 
                      alt={`Logo ${i + 1}`} 
                      style={{
                        width: '60px',
                        height: 'auto',
                        objectFit: 'contain'
                      }}
                    />
                  ))}
                </div>
              )}
            </div>

            <h2 style={{
              margin: '0 0 16px 0',
              fontSize: '1.5rem',
              color: '#333',
              fontWeight: 'bold'
            }}>
              {selectedMilestone.description}
            </h2>

            <p style={{
              margin: 0,
              fontSize: '1rem',
              lineHeight: '1.6',
              color: '#666'
            }}>
              {selectedMilestone.detailedInfo}
            </p>
          </div>
        </div>
      )}

      <style>{`
     
        @keyframes tj-popup-appear {
          from {
            opacity: 0;
            transform: scale(0.9) translateY(20px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        @keyframes tj-bounceArrow {
          0%, 100% {
            transform: translateY(-50%);
          }
          50% {
            transform: translateY(calc(-50% + 10px));
          }
        }

        .tj-scroll-arrow {
          animation: tj-bounceArrow 2s ease-in-out infinite;
        }

        .tj-root * {
          box-sizing: border-box;
        }

        .tj-root .tj-bubble {
          will-change: transform, opacity;
        }

        .tj-root .tj-pin-content {
          will-change: transform, opacity;
        }
        
        .tj-root .tj-pin-marker {
          will-change: transform;
        }

        @media (max-width: 768px) {
          .tj-root .tj-timeline-container {
            height: 500vh !important;
            width: 100% !important;
            max-width: 100vw !important;
            overflow-x: hidden !important;
          }

          .tj-root .tj-sticky-wrapper {
            position: sticky !important;
            top: 0 !important;
            height: 100vh !important;
            padding: 10px !important;
            overflow: hidden !important;
            width: 100% !important;
            max-width: 100vw !important;
            box-sizing: border-box !important;
          }

          .tj-root .tj-road-svg {
            display: block !important;
            transform: translate(-50%, -50%) scale(0.8) !important;
            width: 100% !important;
            left: 50% !important;
          }

          .tj-root .tj-pin-marker {
            position: absolute !important;
          }

          .tj-root .tj-pin-content {
            position: absolute !important;
            width: 110px !important;
            max-width: 130px !important;
            padding: 6px !important;
            font-size: 0.6rem !important;
            margin-top: 0 !important;
            line-height: 1.3 !important;
            gap: 4px !important;
          }

          .tj-root .tj-pin-wrapper {
            width: 40px !important;
            height: 55px !important;
          }

          .tj-root .tj-pin-year {
            font-size: 0.65rem !important;
          }

          .tj-root .tj-map-background {
            opacity: 0.3 !important;
            width: 100% !important;
            left: 50% !important;
          }

          .tj-scroll-arrow span {
            font-size: 12px !important;
          }

          .tj-scroll-arrow > div {
            width: 40px !important;
            height: 40px !important;
          }

          .tj-scroll-arrow svg {
            width: 24px !important;
            height: 24px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default TimelineJourney;